import {
    ReactElement,
    useCallback,
    useEffect,
    useState,
    FormEventHandler,
    useRef
} from 'react';
import { CreateTeamRequest } from '@/api';
import { useCompanyContext } from '../providers';
import { useCreateTeamAction } from '../actions';
import {
    UIModal,
    UIFormInput,
    UIFormInputRef,
    UIModalContent,
    UIModalFooter,
    UIModalHeader
} from '@/ui';
import { useNavigate } from 'react-router';
import { useCompaniesTranslation } from '../hooks';

type CreateTeamModalProps = {
    isOpen: boolean;
    onClose: () => void;
};

export function CreateTeamModal(props: CreateTeamModalProps): ReactElement {
    const { isOpen, onClose } = props;

    const { createTeam, loading } = useCreateTeamAction();
    const { company } = useCompanyContext();
    const navigate = useNavigate();

    function handleClose() {
        onClose();
    }

    function onSubmit(data: CreateTeamRequest) {
        createTeam(data)
            .then((data) => {
                void navigate(
                    `/company/${company.id}/settings/teams/${data.data.team.id}`
                );
            })
            .catch(() => {}); // Error handled in hook
    }

    return (
        <UIModal isOpen={isOpen} onClose={onClose}>
            <CreateTeamModalForm
                companyId={company.id}
                isOpen={isOpen}
                onClose={handleClose}
                loading={loading}
                onSubmit={onSubmit}
            />
        </UIModal>
    );
}

type CreateTeamModalFormProps = {
    companyId: string;
    isOpen: boolean;
    loading: boolean;
    onClose: () => void;
    onSubmit: (data: CreateTeamRequest) => void;
};

function CreateTeamModalForm(props: CreateTeamModalFormProps): ReactElement {
    const { isOpen } = props;

    return <>{isOpen ? <FormContent {...props} /> : null}</>;
}

function FormContent(props: CreateTeamModalFormProps): ReactElement {
    const { companyId, loading, onClose, onSubmit } = props;

    const { t } = useCompaniesTranslation();
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Name
     */
    const nameRef = useRef<UIFormInputRef>(null);
    const nameDefault = '';
    const [name, setName] = useState(nameDefault);
    const [nameError, setNameError] = useState('');
    const checkName = useCallback(
        (name: string): string => {
            const sanitisedName = name.trim();

            if (sanitisedName.length === 0) {
                return t('components.create_team_modal.name_error_required');
            }

            if (sanitisedName.length > 100) {
                return t('components.create_team_modal.name_error_too_long');
            }

            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setNameError(checkName(name));
        }
    }, [name, showRealTimeErrors, checkName]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkName(name)) {
            setShowRealTimeErrors(false);

            onSubmit?.({
                companyId,
                name
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader title={t('components.create_team_modal.title')} />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <UIFormInput
                        ref={nameRef}
                        name="name"
                        label={t('shared.fields.team_name.label')}
                        value={name}
                        description={t(
                            'components.create_team_modal.name_description'
                        )}
                        placeholder={t('shared.fields.team_name.placeholder')}
                        type="text"
                        autoComplete="on"
                        errorText={showRealTimeErrors ? nameError : ''}
                        required
                        maxLength={100}
                        onChange={setName}
                        autoFocus
                    />
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: t('components.create_team_modal.submit')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
